import { useState, useEffect, useCallback } from 'react';
import ContractToolbar from './contract-toolbar';
import ContractTable from './contract-table';
import TemplateLibraryModal from './template-library-modal';
import EditorModal from './editor-modal';
import ViewModal from './view-modal';

import AssignDrawer from './assign-drawer';
import ContractDetailsDrawer from './contract-details';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Worker } from '@react-pdf-viewer/core';
import { Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useToast } from '../../../Utils/toastContext';
import { debounce } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { clearCurrentContractId } from '../../../Redux/store/contractSlice';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// -------------------- MOCK DATA --------------------
const initialContracts = [
  {
    id: 'c1',
    name: 'Oak Residency Lease',
    template: 'Standard Lease',
    content: 'Initial contract content...',
    status: 'Draft',
    progress: 20,
    updatedAt: '2026-06-18',
    assignedUsers: [],
    history: [{ action: 'Created', by: 'System', time: '2026-06-18 10:00' }],
  },
  {
    id: 'c2',
    name: 'Pine Apartment Agreement',
    template: 'Short Form',
    content: 'Agreement content...',
    status: 'Pending Signature',
    progress: 60,
    updatedAt: '2026-06-17',
    assignedUsers: [{ id: 'u1', name: 'Alex Johnson', role: 'Signer' }],
    history: [
      { action: 'Created', by: 'Admin', time: '2026-06-17 09:00' },
      { action: 'Sent for signing', by: 'Admin', time: '2026-06-17 10:00' },
    ],
  },
];
function TemplatePreviewModal({ template, onClose, onContinue, onBack }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white w-full max-w-5xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              {template?.name}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Preview mode (read-only)
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition"
          >
            ✕
          </button>
        </div>

        {/* PDF VIEWER */}
        <div className="flex-1 bg-slate-50 p-4 overflow-hidden">
          <div className="w-full h-full bg-white rounded-xl shadow-sm overflow-hidden">
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                className="w-full h-full"
                fileUrl={template?.previewUrl}
              />
            </Worker>
          </div>
        </div>

        {/* FOOTER ACTION BAR */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-white">
          {/* LEFT */}
          <button
            onClick={onBack}
            className="
          inline-flex items-center gap-2
          px-4 py-2
          text-sm font-medium
          text-slate-600
          border border-slate-200
          rounded-lg
          hover:bg-slate-50
          transition
        "
          >
            ← Back
          </button>

          {/* RIGHT */}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="
            px-4 py-2
            text-sm font-medium
            text-slate-600
            rounded-lg
            hover:bg-slate-100
            transition
          "
            >
              Cancel
            </button>

            <button
              onClick={() => onContinue(template)}
              className="
            px-5 py-2
            text-sm font-medium
            text-white
            rounded-lg
            bg-gradient-to-r from-[#375AE7] to-[#5B7CFF]
            shadow-sm
            hover:shadow-md
            transition
          "
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// -------------------- MAIN --------------------
export default function ContractHub() {
  const [contracts, setContracts] = useState(initialContracts);
  const { showToast } = useToast();
  const { customerId, transactionId } = useParams();
  const [loading, setLoading] = useState(false);
  const [showRecords, setShowRecords] = useState(10);
  const [selectedContract, setSelectedContract] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [isBlank, setIsBlank] = useState(false);
  const [templates, setTemplates] = useState([]);

  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, settotalPages] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [totalCount, settotalCount] = useState(0);
  const [assignOpen, setAssignOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const currentContractId = useSelector((s) => s.contract.currentContractId);
  const [editorContent, setEditorContent] = useState({
    title: 'Property agreement 2026',
    contract_description: null,
    id: 64,
    client_deal_id: 82,
    agency_id: 2,
    contract_source: 'blank',
    envelope_id: '97309063-ef35-485e-a593-f61802bcf762',
    template_version_id: null,
    document_content: {
      pages: [
        {
          id: 'page-1',
          html: '<p><span>Dear </span><span data-placeholder="name" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#EEF1FD] border border-[#375AE7]/20 text-[#375AE7] text-sm font-semibold cursor-pointer" contenteditable="false">Name</span></p><p style="text-align: left;"><span><strong>Letter of Agreement&nbsp;</strong></span></p><p style="text-align: left;"><span>I am writing to offer you, [<em>insert full name of grant recipient and ABN (if applicable</em>)], an Australian Government grant under the [<em>insert grant opportunity name</em>]. The offer is for a grant of $<strong>X </strong>[<em>insert grant amount</em>] total, including $X [<em>insert GST amount</em>] GST, (the ‘Grant’) to undertake the Grant Activity as set out in the attached Grant Schedule.&nbsp;</span></p><p style="text-align: left;"><span>To accept this offer and enter into an agreement with the Commonwealth, represented by [<em>insert full name of Commonwealth entity and ABN</em>] in relation to the Grant, please sign the attached Grant Schedule and send or email a scanned copy to the address below by [<em>insert date</em>], otherwise this offer will lapse.&nbsp;</span></p><p style="text-align: left;"><span>Provided the signed copy of the Grant Schedule is received by the Commonwealth by this date, this letter and the Grant Schedule and the Commonwealth Letter of Agreement Conditions <em>(if applicable</em>) will form a legally enforceable agreement in relation to the Grant.&nbsp;&nbsp;&nbsp;</span></p>',
          content: {
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                attrs: {
                  textAlign: null,
                },
                content: [
                  {
                    text: 'Dear ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    type: 'placeholder',
                    attrs: {
                      id: '633db088-ad31-41cf-9274-52750c77f09a',
                      key: 'name',
                      label: 'Name',
                      value: '',
                    },
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'Letter of Agreement ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'bold',
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'I am writing to offer you, [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert full name of grant recipient and ABN (if applicable',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: ')], an Australian Government grant under the [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert grant opportunity name',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: ']. The offer is for a grant of $',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'X ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'bold',
                      },
                    ],
                  },
                  {
                    text: '[',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert grant amount',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '] total, including $X [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert GST amount',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '] GST, (the ‘Grant’) to undertake the Grant Activity as set out in the attached Grant Schedule. ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'To accept this offer and enter into an agreement with the Commonwealth, represented by [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert full name of Commonwealth entity and ABN',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '] in relation to the Grant, please sign the attached Grant Schedule and send or email a scanned copy to the address below by [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert date',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '], otherwise this offer will lapse. ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'Provided the signed copy of the Grant Schedule is received by the Commonwealth by this date, this letter and the Grant Schedule and the Commonwealth Letter of Agreement Conditions ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: '(if applicable',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: ') will form a legally enforceable agreement in relation to the Grant.   ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        },
        {
          id: 'page-1783636134740',
          html: '<div class="special-conditions-block">Special Conditions</div><p><span>Please send or email a scanned copy of the signed and completed Grant Schedule to:&nbsp;<br></span><span data-placeholder="email" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#EEF1FD] border border-[#375AE7]/20 text-[#375AE7] text-sm font-semibold cursor-pointer" contenteditable="false">Email</span><br><br><span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed border-rose-300 bg-rose-50/60 text-sm font-medium text-rose-700">Sign here</span><br><br><span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-sm font-medium text-amber-800">Date</span></p>',
          content: {
            type: 'doc',
            content: [
              {
                type: 'specialConditions',
                attrs: {
                  id: '06b32659-a0e8-4c05-b1e4-cf0b61394c81',
                  title: 'Special Conditions',
                  expanded: true,
                },
                content: [
                  {
                    type: 'bulletList',
                    content: [
                      {
                        type: 'listItem',
                        content: [
                          {
                            type: 'paragraph',
                            attrs: {
                              textAlign: null,
                            },
                            content: [
                              {
                                text: 'The parties acknowledge and agree that they are each registered for GST purposes, have each quoted their Australian Business Number to the other and must notify the other of any changes in their GST status.  The Grantee agrees that the Commonwealth will issue the Grantee with a recipient created tax invoice, and the Grantee will not issue any tax invoices, for any taxable supply the Grantee makes under this Agreement. ',
                                type: 'text',
                                marks: [
                                  {
                                    type: 'textStyle',
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: null,
                },
                content: [
                  {
                    text: 'Please send or email a scanned copy of the signed and completed Grant Schedule to: ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    type: 'hardBreak',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    type: 'placeholder',
                    attrs: {
                      id: '9d68749e-2a13-4464-90b7-77acf69fdc22',
                      key: 'email',
                      label: 'Email',
                      value: '',
                    },
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'signature',
                    attrs: {
                      id: 'ddf81923-34c4-49d2-a8d4-46a673dc5570',
                      label: 'Sign',
                      value: '',
                    },
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'dateField',
                    attrs: {
                      id: 'ae90b765-177a-4048-b392-a6f066715726',
                      value: '',
                    },
                  },
                ],
              },
            ],
          },
        },
      ],
      comments: [],
    },
    placeholder_values: {},
    signing_flow: [],
    document_snapshot: {
      pages: [
        {
          id: 'page-1',
          html: '<p><span>Dear </span><span data-placeholder="name" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#EEF1FD] border border-[#375AE7]/20 text-[#375AE7] text-sm font-semibold cursor-pointer" contenteditable="false">Name</span></p><p style="text-align: left;"><span><strong>Letter of Agreement&nbsp;</strong></span></p><p style="text-align: left;"><span>I am writing to offer you, [<em>insert full name of grant recipient and ABN (if applicable</em>)], an Australian Government grant under the [<em>insert grant opportunity name</em>]. The offer is for a grant of $<strong>X </strong>[<em>insert grant amount</em>] total, including $X [<em>insert GST amount</em>] GST, (the ‘Grant’) to undertake the Grant Activity as set out in the attached Grant Schedule.&nbsp;</span></p><p style="text-align: left;"><span>To accept this offer and enter into an agreement with the Commonwealth, represented by [<em>insert full name of Commonwealth entity and ABN</em>] in relation to the Grant, please sign the attached Grant Schedule and send or email a scanned copy to the address below by [<em>insert date</em>], otherwise this offer will lapse.&nbsp;</span></p><p style="text-align: left;"><span>Provided the signed copy of the Grant Schedule is received by the Commonwealth by this date, this letter and the Grant Schedule and the Commonwealth Letter of Agreement Conditions <em>(if applicable</em>) will form a legally enforceable agreement in relation to the Grant.&nbsp;&nbsp;&nbsp;</span></p>',
          content: {
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                attrs: {
                  textAlign: null,
                },
                content: [
                  {
                    text: 'Dear ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    type: 'placeholder',
                    attrs: {
                      id: '633db088-ad31-41cf-9274-52750c77f09a',
                      key: 'name',
                      label: 'Name',
                      value: '',
                    },
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'Letter of Agreement ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'bold',
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'I am writing to offer you, [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert full name of grant recipient and ABN (if applicable',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: ')], an Australian Government grant under the [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert grant opportunity name',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: ']. The offer is for a grant of $',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'X ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'bold',
                      },
                    ],
                  },
                  {
                    text: '[',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert grant amount',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '] total, including $X [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert GST amount',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '] GST, (the ‘Grant’) to undertake the Grant Activity as set out in the attached Grant Schedule. ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'To accept this offer and enter into an agreement with the Commonwealth, represented by [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert full name of Commonwealth entity and ABN',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '] in relation to the Grant, please sign the attached Grant Schedule and send or email a scanned copy to the address below by [',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: 'insert date',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: '], otherwise this offer will lapse. ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                },
                content: [
                  {
                    text: 'Provided the signed copy of the Grant Schedule is received by the Commonwealth by this date, this letter and the Grant Schedule and the Commonwealth Letter of Agreement Conditions ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    text: '(if applicable',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                      {
                        type: 'italic',
                      },
                    ],
                  },
                  {
                    text: ') will form a legally enforceable agreement in relation to the Grant.   ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        },
        {
          id: 'page-1783636134740',
          html: '<div class="special-conditions-block">Special Conditions</div><p><span>Please send or email a scanned copy of the signed and completed Grant Schedule to:&nbsp;<br></span><span data-placeholder="email" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#EEF1FD] border border-[#375AE7]/20 text-[#375AE7] text-sm font-semibold cursor-pointer" contenteditable="false">Email</span><br><br><span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed border-rose-300 bg-rose-50/60 text-sm font-medium text-rose-700">Sign here</span><br><br><span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-sm font-medium text-amber-800">Date</span></p>',
          content: {
            type: 'doc',
            content: [
              {
                type: 'specialConditions',
                attrs: {
                  id: '06b32659-a0e8-4c05-b1e4-cf0b61394c81',
                  title: 'Special Conditions',
                  expanded: true,
                },
                content: [
                  {
                    type: 'bulletList',
                    content: [
                      {
                        type: 'listItem',
                        content: [
                          {
                            type: 'paragraph',
                            attrs: {
                              textAlign: null,
                            },
                            content: [
                              {
                                text: 'The parties acknowledge and agree that they are each registered for GST purposes, have each quoted their Australian Business Number to the other and must notify the other of any changes in their GST status.  The Grantee agrees that the Commonwealth will issue the Grantee with a recipient created tax invoice, and the Grantee will not issue any tax invoices, for any taxable supply the Grantee makes under this Agreement. ',
                                type: 'text',
                                marks: [
                                  {
                                    type: 'textStyle',
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                type: 'paragraph',
                attrs: {
                  textAlign: null,
                },
                content: [
                  {
                    text: 'Please send or email a scanned copy of the signed and completed Grant Schedule to: ',
                    type: 'text',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    type: 'hardBreak',
                    marks: [
                      {
                        type: 'textStyle',
                      },
                    ],
                  },
                  {
                    type: 'placeholder',
                    attrs: {
                      id: '9d68749e-2a13-4464-90b7-77acf69fdc22',
                      key: 'email',
                      label: 'Email',
                      value: '',
                    },
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'signature',
                    attrs: {
                      id: 'ddf81923-34c4-49d2-a8d4-46a673dc5570',
                      label: 'Sign',
                      value: '',
                    },
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'dateField',
                    attrs: {
                      id: 'ae90b765-177a-4048-b392-a6f066715726',
                      value: '',
                    },
                  },
                ],
              },
            ],
          },
        },
      ],
      approved_at: '2026-07-09T22:30:27.409795+00:00',
      signing_flow: [],
      source_file_id: null,
      contract_source: 'blank',
      placeholder_values: {},
      template_version_id: null,
    },
    source_file_id: null,
    document_hash:
      '580660014854cffeb3a47a13d740e847383c5e0a4e3976a98c8b77580825d6ab',
    rendered_pdf_file_id: '2d6ee70a-010e-4d74-b6a0-4708c4a90dcc',
    post_sign_document_hash: null,
    status: 'completed',
    signer_recipients: [
      {
        name: 'Individual Customer',
        email: 'individualcustomer@gmail.com',
        phone: '+61412345678',
        role: 'tenant',
        user_id: 52,
        status: 'signed',
        signed_at: '2026-07-09T22:32:56.282926Z',
        viewed_at: '2026-07-09T22:32:24.664511Z',
        has_signed: true,
        signed_fields: [
          {
            field_id: 'sig-64',
            field_type: 'signature',
            page_number: 1,
            x: 0,
            y: 0,
            w: 0,
            h: 0,
            label: 'Signature',
            value: null,
            signer_user_id: 52,
            signature_image_file_id: 'a6ad5513-4483-49c9-8497-40df11f42142',
            signature_image_url:
              'https://dev-ratified-docs.s3.amazonaws.com/signatures/2/97309063-ef35-485e-a593-f61802bcf762/90301684-9afc-43a0-ac9a-f9cbd7137679/sig-64_a6ad5513-4483-49c9-8497-40df11f42142.png?response-content-disposition=inline&response-content-type=image%2Fpng&AWSAccessKeyId=ASIAZZTJYOVJOLKXQFJD&Signature=4kdZ0MOPyF840LBI9H%2BJ4chW0BY%3D&x-amz-security-token=IQoJb3JpZ2luX2VjEG4aDmFwLXNvdXRoZWFzdC0yIkgwRgIhALJxr6uvf91kCCLjE3Z89ItO0N7SCxCVA9dqEadtakm%2BAiEAqltRIntuNrPxvJ6%2FdrJfiW4%2Fj%2FPVmp1uAvEBXr4Ij80qywUINxAAGgw2NzM0NTc5Mjc1MDYiDFE5OW34eO3cMc%2FxsiqoBYhC7GxMYgqwch7jpnPmagIhH4AC%2Fp91xTXg8YqsBNDozFXg665n44y2%2BCXCHE7EhTQSEqKELGfC9sTtwW5FG604Pee%2B%2BH7BMtMyIMbmsMr3kuExy%2Bt5Mka4u4uoP4uyrFa73XWODEPCDwaNqgEfxtgErwYDx4yi6J3brvCJSgt0HV%2FhryURfjF%2F8sln0LKyDI1Etosj3YUrkK4bn8h8cbL9zabeY86fhwmV27BYkUS0zZ5iwZb3U5%2FalHfUjsmrHxoO1Avs3aseH50toxlKsUkBRgexFHsWc9Yiat4Kl%2F%2Fe19Yu1jar38mCsObKdIB%2BAmrNEiCOHX2h8SP6qAw3OseJ2dG1L4GDIwdKaw%2Bx42SFONgmvMkXGr8a%2FpiSTg7fERVsyVhqr3OwdSh5vR%2B%2BsC4o1jXRJzuegB8MS7cHhMYwvXpR5OMWIJQcrAhsu8axrpLVrk5pF4tmApRvteyivhAETQgps7ZKHK1rme5S7Y%2BwAFkwyRdkbbjbDj96qD3hIHmN0dbf15MP9GekrkwWLHpHUKRP4bpDINQ3QYEh4qqvJyVYvZH8EnfoNmIc6STrx21CLcQdelvY6EB2f0EDJDBspN1qxY%2FHHVnhtbfZ9BaRadfAP%2FUcKs1l9ddH%2F5A7OHrglkCDiISulpbB9scb0uws9NPVkvKQOxXhyycO%2FkHFSVAnrSJiR46FgpmjBBezaqAJ4i7bzUIYSIYq2llKJIjtvBNrK2pHyb%2F818nQ8CY65ZS%2BsROgk6roJLimvKfDLx13Ydt9B2wHsiQPNfUf1BoK6VB2XXhhXhd59lPhu67oQ5zSfMVIXCm1H4eiU9Ldvi5llu7dbxKahktt3SBTVn4bOJdPQj9xR19s3o4Wx3IlCLj9QMna1MCLwmrmXhiJFeGNfkszuFNTMI6E4NIGOrAB0rN3Y6gf8zj75QY2aGoiH37TNIQkBQdPSb5a3oYCmMQpj823rkrfroHYpYrOFSqp78FFdkWDYZNGoUdyfQIAbg0mpDK4RClQpTLQyt2jQR5AKFlYMirh0zNgKggmMgzqsSSRiRlmybKlpPfDjVQi5I6OLN64J0MLrPo8Hl5b8I1VHhlMRSZUPdEOnpV9OOOjfibzqSzQpj%2BfOWTQLPdY2fvQtj8uXnoADH5NP7cNbZ0%3D&Expires=1784161795',
            signed_at: '2026-07-09T22:32:56.270809+00:00',
          },
        ],
      },
    ],
    can_edit: false,
    version_number: 2,
    pdf_url:
      'https://dev-ratified-docs.s3.amazonaws.com/evidence_packs/2/97309063-ef35-485e-a593-f61802bcf762/sealed_55098a8e-5980-45ca-8524-448684bddae2.pdf?response-content-disposition=inline&response-content-type=application%2Fpdf&AWSAccessKeyId=ASIAZZTJYOVJOLKXQFJD&Signature=cuQxIYQhviNT7PKVq92jqE4TktU%3D&x-amz-security-token=IQoJb3JpZ2luX2VjEG4aDmFwLXNvdXRoZWFzdC0yIkgwRgIhALJxr6uvf91kCCLjE3Z89ItO0N7SCxCVA9dqEadtakm%2BAiEAqltRIntuNrPxvJ6%2FdrJfiW4%2Fj%2FPVmp1uAvEBXr4Ij80qywUINxAAGgw2NzM0NTc5Mjc1MDYiDFE5OW34eO3cMc%2FxsiqoBYhC7GxMYgqwch7jpnPmagIhH4AC%2Fp91xTXg8YqsBNDozFXg665n44y2%2BCXCHE7EhTQSEqKELGfC9sTtwW5FG604Pee%2B%2BH7BMtMyIMbmsMr3kuExy%2Bt5Mka4u4uoP4uyrFa73XWODEPCDwaNqgEfxtgErwYDx4yi6J3brvCJSgt0HV%2FhryURfjF%2F8sln0LKyDI1Etosj3YUrkK4bn8h8cbL9zabeY86fhwmV27BYkUS0zZ5iwZb3U5%2FalHfUjsmrHxoO1Avs3aseH50toxlKsUkBRgexFHsWc9Yiat4Kl%2F%2Fe19Yu1jar38mCsObKdIB%2BAmrNEiCOHX2h8SP6qAw3OseJ2dG1L4GDIwdKaw%2Bx42SFONgmvMkXGr8a%2FpiSTg7fERVsyVhqr3OwdSh5vR%2B%2BsC4o1jXRJzuegB8MS7cHhMYwvXpR5OMWIJQcrAhsu8axrpLVrk5pF4tmApRvteyivhAETQgps7ZKHK1rme5S7Y%2BwAFkwyRdkbbjbDj96qD3hIHmN0dbf15MP9GekrkwWLHpHUKRP4bpDINQ3QYEh4qqvJyVYvZH8EnfoNmIc6STrx21CLcQdelvY6EB2f0EDJDBspN1qxY%2FHHVnhtbfZ9BaRadfAP%2FUcKs1l9ddH%2F5A7OHrglkCDiISulpbB9scb0uws9NPVkvKQOxXhyycO%2FkHFSVAnrSJiR46FgpmjBBezaqAJ4i7bzUIYSIYq2llKJIjtvBNrK2pHyb%2F818nQ8CY65ZS%2BsROgk6roJLimvKfDLx13Ydt9B2wHsiQPNfUf1BoK6VB2XXhhXhd59lPhu67oQ5zSfMVIXCm1H4eiU9Ldvi5llu7dbxKahktt3SBTVn4bOJdPQj9xR19s3o4Wx3IlCLj9QMna1MCLwmrmXhiJFeGNfkszuFNTMI6E4NIGOrAB0rN3Y6gf8zj75QY2aGoiH37TNIQkBQdPSb5a3oYCmMQpj823rkrfroHYpYrOFSqp78FFdkWDYZNGoUdyfQIAbg0mpDK4RClQpTLQyt2jQR5AKFlYMirh0zNgKggmMgzqsSSRiRlmybKlpPfDjVQi5I6OLN64J0MLrPo8Hl5b8I1VHhlMRSZUPdEOnpV9OOOjfibzqSzQpj%2BfOWTQLPdY2fvQtj8uXnoADH5NP7cNbZ0%3D&Expires=1784161795',
    signed_pdf_url:
      'https://dev-ratified-docs.s3.amazonaws.com/evidence_packs/2/97309063-ef35-485e-a593-f61802bcf762/sealed_55098a8e-5980-45ca-8524-448684bddae2.pdf?response-content-disposition=inline&response-content-type=application%2Fpdf&AWSAccessKeyId=ASIAZZTJYOVJOLKXQFJD&Signature=cuQxIYQhviNT7PKVq92jqE4TktU%3D&x-amz-security-token=IQoJb3JpZ2luX2VjEG4aDmFwLXNvdXRoZWFzdC0yIkgwRgIhALJxr6uvf91kCCLjE3Z89ItO0N7SCxCVA9dqEadtakm%2BAiEAqltRIntuNrPxvJ6%2FdrJfiW4%2Fj%2FPVmp1uAvEBXr4Ij80qywUINxAAGgw2NzM0NTc5Mjc1MDYiDFE5OW34eO3cMc%2FxsiqoBYhC7GxMYgqwch7jpnPmagIhH4AC%2Fp91xTXg8YqsBNDozFXg665n44y2%2BCXCHE7EhTQSEqKELGfC9sTtwW5FG604Pee%2B%2BH7BMtMyIMbmsMr3kuExy%2Bt5Mka4u4uoP4uyrFa73XWODEPCDwaNqgEfxtgErwYDx4yi6J3brvCJSgt0HV%2FhryURfjF%2F8sln0LKyDI1Etosj3YUrkK4bn8h8cbL9zabeY86fhwmV27BYkUS0zZ5iwZb3U5%2FalHfUjsmrHxoO1Avs3aseH50toxlKsUkBRgexFHsWc9Yiat4Kl%2F%2Fe19Yu1jar38mCsObKdIB%2BAmrNEiCOHX2h8SP6qAw3OseJ2dG1L4GDIwdKaw%2Bx42SFONgmvMkXGr8a%2FpiSTg7fERVsyVhqr3OwdSh5vR%2B%2BsC4o1jXRJzuegB8MS7cHhMYwvXpR5OMWIJQcrAhsu8axrpLVrk5pF4tmApRvteyivhAETQgps7ZKHK1rme5S7Y%2BwAFkwyRdkbbjbDj96qD3hIHmN0dbf15MP9GekrkwWLHpHUKRP4bpDINQ3QYEh4qqvJyVYvZH8EnfoNmIc6STrx21CLcQdelvY6EB2f0EDJDBspN1qxY%2FHHVnhtbfZ9BaRadfAP%2FUcKs1l9ddH%2F5A7OHrglkCDiISulpbB9scb0uws9NPVkvKQOxXhyycO%2FkHFSVAnrSJiR46FgpmjBBezaqAJ4i7bzUIYSIYq2llKJIjtvBNrK2pHyb%2F818nQ8CY65ZS%2BsROgk6roJLimvKfDLx13Ydt9B2wHsiQPNfUf1BoK6VB2XXhhXhd59lPhu67oQ5zSfMVIXCm1H4eiU9Ldvi5llu7dbxKahktt3SBTVn4bOJdPQj9xR19s3o4Wx3IlCLj9QMna1MCLwmrmXhiJFeGNfkszuFNTMI6E4NIGOrAB0rN3Y6gf8zj75QY2aGoiH37TNIQkBQdPSb5a3oYCmMQpj823rkrfroHYpYrOFSqp78FFdkWDYZNGoUdyfQIAbg0mpDK4RClQpTLQyt2jQR5AKFlYMirh0zNgKggmMgzqsSSRiRlmybKlpPfDjVQi5I6OLN64J0MLrPo8Hl5b8I1VHhlMRSZUPdEOnpV9OOOjfibzqSzQpj%2BfOWTQLPdY2fvQtj8uXnoADH5NP7cNbZ0%3D&Expires=1784161795',
    evidence_pre_signing_hash:
      'b1fae5d3d03df54b18f9931d224888fd759750e0f58c91b7165d03cfc1abfdb7',
    evidence_post_signing_hash:
      '4578ac2f5affd1056b51b1790f871b0fc86623dd90aae4a65648076f8543c8ef',
    customer_status: 'signed',
    decline_reason: null,
    approved_by: 44,
    approved_at: '2026-07-09T22:30:27.411671Z',
    reject_reason: null,
    signing_decline_reason: null,
    created_at: '2026-07-09T22:30:27.006701Z',
    updated_at: '2026-07-09T22:32:55.659409Z',
    events: [
      {
        id: 194,
        deal_contract_id: 64,
        event_type: 'contract_created',
        event_payload: {
          title: 'Property agreement 2026',
          signer_count: 0,
          contract_source: 'blank',
        },
        actor_user_id: null,
        actor_name: 'Team Member',
        actor_role: 'agent',
        ip_address: null,
        created_at: '2026-07-09T22:30:27.006701Z',
      },
      {
        id: 195,
        deal_contract_id: 64,
        event_type: 'contract_approved',
        event_payload: {},
        actor_user_id: null,
        actor_name: 'Team Member',
        actor_role: 'agent',
        ip_address: null,
        created_at: '2026-07-09T22:30:27.392227Z',
      },
      {
        id: 196,
        deal_contract_id: 64,
        event_type: 'envelope_sent',
        event_payload: {
          envelope_id: '97309063-ef35-485e-a593-f61802bcf762',
          delivery_method: 'in_app',
          recipient_user_ids: [52],
          rendered_pdf_file_id: '2d6ee70a-010e-4d74-b6a0-4708c4a90dcc',
        },
        actor_user_id: null,
        actor_name: 'Team Member',
        actor_role: 'agent',
        ip_address: null,
        created_at: '2026-07-09T22:30:33.479820Z',
      },
      {
        id: 197,
        deal_contract_id: 64,
        event_type: 'status_synced_completed',
        event_payload: {
          docsign_status: 'completed',
          previous_status: 'sent',
        },
        actor_user_id: null,
        actor_name: 'System Sync',
        actor_role: 'system',
        ip_address: null,
        created_at: '2026-07-09T22:32:55.659409Z',
      },
      {
        id: 198,
        deal_contract_id: 64,
        event_type: 'customer_signed',
        event_payload: {
          envelope_id: '97309063-ef35-485e-a593-f61802bcf762',
        },
        actor_user_id: null,
        actor_name: 'Customer',
        actor_role: 'customer',
        ip_address: null,
        created_at: '2026-07-09T22:32:55.659409Z',
      },
    ],
    signed_fields: [
      {
        field_id: 'sig-64',
        field_type: 'signature',
        page_number: 1,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        label: 'Signature',
        value: null,
        signer_user_id: 52,
        signature_image_file_id: 'a6ad5513-4483-49c9-8497-40df11f42142',
        signature_image_url:
          'https://dev-ratified-docs.s3.amazonaws.com/signatures/2/97309063-ef35-485e-a593-f61802bcf762/90301684-9afc-43a0-ac9a-f9cbd7137679/sig-64_a6ad5513-4483-49c9-8497-40df11f42142.png?response-content-disposition=inline&response-content-type=image%2Fpng&AWSAccessKeyId=ASIAZZTJYOVJOLKXQFJD&Signature=4kdZ0MOPyF840LBI9H%2BJ4chW0BY%3D&x-amz-security-token=IQoJb3JpZ2luX2VjEG4aDmFwLXNvdXRoZWFzdC0yIkgwRgIhALJxr6uvf91kCCLjE3Z89ItO0N7SCxCVA9dqEadtakm%2BAiEAqltRIntuNrPxvJ6%2FdrJfiW4%2Fj%2FPVmp1uAvEBXr4Ij80qywUINxAAGgw2NzM0NTc5Mjc1MDYiDFE5OW34eO3cMc%2FxsiqoBYhC7GxMYgqwch7jpnPmagIhH4AC%2Fp91xTXg8YqsBNDozFXg665n44y2%2BCXCHE7EhTQSEqKELGfC9sTtwW5FG604Pee%2B%2BH7BMtMyIMbmsMr3kuExy%2Bt5Mka4u4uoP4uyrFa73XWODEPCDwaNqgEfxtgErwYDx4yi6J3brvCJSgt0HV%2FhryURfjF%2F8sln0LKyDI1Etosj3YUrkK4bn8h8cbL9zabeY86fhwmV27BYkUS0zZ5iwZb3U5%2FalHfUjsmrHxoO1Avs3aseH50toxlKsUkBRgexFHsWc9Yiat4Kl%2F%2Fe19Yu1jar38mCsObKdIB%2BAmrNEiCOHX2h8SP6qAw3OseJ2dG1L4GDIwdKaw%2Bx42SFONgmvMkXGr8a%2FpiSTg7fERVsyVhqr3OwdSh5vR%2B%2BsC4o1jXRJzuegB8MS7cHhMYwvXpR5OMWIJQcrAhsu8axrpLVrk5pF4tmApRvteyivhAETQgps7ZKHK1rme5S7Y%2BwAFkwyRdkbbjbDj96qD3hIHmN0dbf15MP9GekrkwWLHpHUKRP4bpDINQ3QYEh4qqvJyVYvZH8EnfoNmIc6STrx21CLcQdelvY6EB2f0EDJDBspN1qxY%2FHHVnhtbfZ9BaRadfAP%2FUcKs1l9ddH%2F5A7OHrglkCDiISulpbB9scb0uws9NPVkvKQOxXhyycO%2FkHFSVAnrSJiR46FgpmjBBezaqAJ4i7bzUIYSIYq2llKJIjtvBNrK2pHyb%2F818nQ8CY65ZS%2BsROgk6roJLimvKfDLx13Ydt9B2wHsiQPNfUf1BoK6VB2XXhhXhd59lPhu67oQ5zSfMVIXCm1H4eiU9Ldvi5llu7dbxKahktt3SBTVn4bOJdPQj9xR19s3o4Wx3IlCLj9QMna1MCLwmrmXhiJFeGNfkszuFNTMI6E4NIGOrAB0rN3Y6gf8zj75QY2aGoiH37TNIQkBQdPSb5a3oYCmMQpj823rkrfroHYpYrOFSqp78FFdkWDYZNGoUdyfQIAbg0mpDK4RClQpTLQyt2jQR5AKFlYMirh0zNgKggmMgzqsSSRiRlmybKlpPfDjVQi5I6OLN64J0MLrPo8Hl5b8I1VHhlMRSZUPdEOnpV9OOOjfibzqSzQpj%2BfOWTQLPdY2fvQtj8uXnoADH5NP7cNbZ0%3D&Expires=1784161795',
        signed_at: '2026-07-09T22:32:56.270809+00:00',
      },
    ],
    versions: [
      {
        version_number: 2,
        source: 'approve',
        label: 'Approved snapshot',
        created_by: 44,
        created_at: '2026-07-09T22:30:27.392227Z',
      },
      {
        version_number: 1,
        source: 'create',
        label: null,
        created_by: 44,
        created_at: '2026-07-09T22:30:27.006701Z',
      },
    ],
  });
  const navigate = useNavigate();
  const location = useLocation();

  // ---------------- CREATE / EDIT SAVE ----------------
  function handleSave(content) {
    setEditorOpen(false);
    setAssignOpen(true);
    getContracts();
  }

  // function handleAssign(usersWithRoles) {
  //   setContracts((prev) =>
  //     prev.map((c) =>
  //       c.id === selectedContract.id
  //         ? {
  //             ...c,
  //             assignedUsers: usersWithRoles.selectedUsers,
  //             status: "Pending Signature",
  //             progress: 70,
  //             history: [
  //               ...c.history,
  //               {
  //                 action: "Assigned",
  //                 by: "User",
  //                 time: new Date().toISOString(),
  //               },
  //             ],
  //           }
  //         : c,
  //     ),
  //   );

  //   setAssignOpen(false);
  // }
  const saveDocAPI = async (usersWithRoles) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/agent/customers/${customerId}/client-deals/${transactionId}/contracts/${currentContractId}/send-for-signing`;
    console.log(usersWithRoles);
    let recipient_user_ids = usersWithRoles.selectedUsers.map(
      (user) => user.user_id,
    );
    const payload = {
      message: 'Please review and sign this agreement.',
      delivery_method: 'in_app',
      signing_order: 'parallel',
      expires_days: 30,
      recipient_user_ids: recipient_user_ids,
    };
    const bodyData = payload;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  // ---------------- ASSIGN USERS ----------------

  const handleAssign = useCallback(
    debounce(async (usersWithRoles) => {
      let toastMessage = null;
      try {
        setLoading(true);
        const response = await saveDocAPI(usersWithRoles);
        if (response.status == 201) {
          showToast(response.message, 'success');
          getContracts();
          setAssignOpen(false);
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [selectedContract, currentContractId, customerId, transactionId], // Dependencies
  );

  // ---------------- VIEW DETAILS ----------------
  function handleView(contract) {
    setSelectedContract(contract);
    navigate(`/contract-detail/${customerId}/${transactionId}/${contract.id}`, {
      state: {
        contract,
        from: location.pathname,
        historyStack: [
          ...(location.state?.historyStack || []),
          location.pathname,
        ],
      },
    });
  }
  function handleEdit(contract) {
    setSelectedContract(contract);
    navigate('/edit-template/1', {
      state: {
        contract,
        from: location.pathname,
        historyStack: [
          ...(location.state?.historyStack || []),
          location.pathname,
        ],
      },
    });
  }
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };

  const getTemplates = async () => {
    try {
      const response = await GetAPIcalls('/api/v1/agent/contract-library');

      if (response.status == 200) {
        setTemplates(response.data.contracts);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getContracts = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agent/customers/${customerId}/client-deals/${transactionId}/contracts?page=${currentPage}&page_size=10&search=${filterText}`,
      );

      if (response.status == 200) {
        setContracts(response.data.contracts || []);
        settotalPages(response.data.pagination.total_pages);
        settotalCount(response.data.pagination.total);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    getTemplates();
  }, []);
  useEffect(() => {
    getContracts();
    dispatch(clearCurrentContractId());
  }, [currentPage, filterText]);
  return (
    <>
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="mr-10 rounded-lg bg-gray-50 p-6 space-y-6">
        {/* TOOLBAR */}
        <ContractToolbar
          onAdd={() => setTemplateModalOpen(true)}
          onSearch={(value) => {
            setFilterText(value);
            setCurrentPage(1);
          }}
        />

        {/* TABLE */}
        <ContractTable
          contracts={contracts}
          onView={handleView}
          onEdit={(c) => {
            // handleEdit(c);
            setEditorContent(`## ${c.name}\n\nStart writing contract...`);
            setEditorOpen(true);
            setIsBlank(false);
          }}
          onAssign={(c) => {
            setSelectedContract(c);
            setAssignOpen(true);
          }}
        />
        {contracts.length > 0 && (
          <div className="flex flex-wrap justify-between items-center mt-4 space-y-4 sm:space-y-0">
            {/* Entries Info Section */}
            <div className="text-[#8C8C8C] text-sm sm:text-base w-full sm:w-auto text-center sm:text-left">
              Showing {showRecords * (currentPage - 1) + 1} to{' '}
              {currentPage === totalPages || totalPages == 0
                ? totalCount
                : Math.max(showRecords * currentPage, contracts.length)}{' '}
              of {totalCount} entries
            </div>

            {/* Pagination Section */}
            <div className="flex flex-wrap items-center justify-center sm:justify-end font-semibold text-sm gap-2 w-full sm:w-auto">
              {/* Previous Button */}
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>

              {/* Page Buttons */}
              {Array.from({ length: Math.min(totalPages, 5) }).map(
                (_, index) => {
                  const startPage = Math.max(
                    1,
                    Math.min(totalPages - 4, currentPage - 2),
                  );
                  const page = startPage + index;

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded ${
                        currentPage === page
                          ? 'bg-[#375AE7] text-white'
                          : 'bg-white border border-gray-300 text-black'
                      }`}
                    >
                      {page}
                    </button>
                  );
                },
              )}

              {/* Next Button */}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      {/* TEMPLATE MODAL */}
      {templateModalOpen && (
        <TemplateLibraryModal
          templates={templates}
          onClose={() => {
            setTemplateModalOpen(false);
            dispatch(clearCurrentContractId());
          }}
          onSelectTemplate={(t) => {
            setTemplateModalOpen(false);
            let redirectState = {
              path: `/view-agent-contract/${customerId}/${transactionId}/${t.id}`,
              state: {
                from: location.pathname,
                historyStack: [
                  ...(location.state?.historyStack || []),
                  location.pathname,
                ],
              },
            };
            sessionStorage.setItem(
              'redirectAfterLogin',
              JSON.stringify(redirectState),
            );
            navigate(
              `/view-agent-contract/${customerId}/${transactionId}/${t.id}`,
              {
                state: {
                  from: location.pathname,
                  historyStack: [
                    ...(location.state?.historyStack || []),
                    location.pathname,
                  ],
                },
              },
            );
          }}
          onCreateBlank={() => {
            setTemplateModalOpen(false);
            //setSelectedContract(null);
            setIsBlank(true);
            setEditorContent('');
            setEditorOpen(true);
          }}
        />
      )}

      {/* EDITOR */}
      {/* {editorOpen && (
        <EditorModal
          content={editorContent}
          setContent={setEditorContent}
          onClose={() => {
            setEditorOpen(false);
            setIsBlank(false);
            dispatch(clearCurrentContractId());
          }}
          onSave={handleSave}
          isBlank={isBlank}
          contract={selectedContract}
          isEdit={false}
        />
      )} */}
      {editorOpen && (
        <ViewModal
          content={editorContent}
          setContent={setEditorContent}
          onClose={() => {
            setEditorOpen(false);
            setIsBlank(false);
            dispatch(clearCurrentContractId());
          }}
          onSave={handleSave}
          isBlank={isBlank}
          contract={editorContent}
          isEdit={false}
        />
      )}

      {/* ASSIGN */}
      {assignOpen && (
        <AssignDrawer
          contract={selectedContract}
          onClose={() => {
            setAssignOpen(false);
            dispatch(clearCurrentContractId());
          }}
          onDone={handleAssign}
          isBack={true}
          setBack={() => {
            setAssignOpen(false);
            setEditorOpen(true);
          }}
        />
      )}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => {
            setPreviewTemplate(null);
            dispatch(clearCurrentContractId());
          }}
          onBack={() => {
            setPreviewTemplate(null);

            setTemplateModalOpen(true);
            dispatch(clearCurrentContractId());
          }}
          onContinue={(t) => {
            setPreviewTemplate(t);
            setPreviewTemplate(null);
            setSelectedContract(t);
            setAssignOpen(true);
          }}
        />
      )}
    </>
  );
}
